'use client';

import { useEffect, useState } from 'react';

interface SessionTimeoutWarningProps {
  remainingMs: number;
  onStay: () => void;
}

export function SessionTimeoutWarning({ remainingMs, onStay }: SessionTimeoutWarningProps) {
  const [secondsLeft, setSecondsLeft] = useState(() => Math.ceil(remainingMs / 1000));
  // Reset countdown when parent passes a new remainingMs (e.g. user activity
  // bumped the deadline). React-documented "store info from previous renders"
  // pattern — setState during render is fine when guarded by a comparison;
  // React discards the in-progress render and re-renders with the new state.
  const [lastRemainingMs, setLastRemainingMs] = useState(remainingMs);
  if (lastRemainingMs !== remainingMs) {
    setLastRemainingMs(remainingMs);
    setSecondsLeft(Math.ceil(remainingMs / 1000));
  }

  useEffect(() => {
    const id = setInterval(() => {
      setSecondsLeft((s) => Math.max(0, s - 1));
    }, 1000);
    return () => clearInterval(id);
  }, []);

  return (
    <div
      role="alertdialog"
      aria-labelledby="session-warning-title"
      aria-describedby="session-warning-desc"
      className="fixed inset-0 z-9999 flex items-center justify-center bg-black/80 backdrop-blur-sm"
    >
      <div className="mx-4 w-full max-w-md rounded-lg bg-white p-8 shadow-2xl">
        <div className="mb-2 text-center text-4xl" aria-hidden="true">
          ⏳
        </div>
        <h2
          id="session-warning-title"
          className="mb-2 text-center text-xl font-semibold text-gray-900"
        >
          Session Timing Out
        </h2>
        <p id="session-warning-desc" className="mb-6 text-center text-sm text-gray-600">
          Your session will lock in {secondsLeft} second{secondsLeft === 1 ? '' : 's'} due to inactivity.
        </p>

        <button
          onClick={onStay}
          autoFocus
          className="w-full rounded-md bg-orange-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-orange-700"
        >
          Stay Logged In
        </button>
      </div>
    </div>
  );
}
